import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ServiceEndpoints from '../services/ServiceEndpoints';
import { useAuth } from 'react-oidc-context';
import { fetchWithAuth } from '../utils/fetchUtils';

// Define the shape of the app settings
interface SanitizationSettings {
  invalidCharactersPattern: string;
  invalidCharactersRegexTimeoutMs: number;
  throwOnInvalidInput: boolean;
  enabledSanitizationFilter: boolean;
}

interface AppSettings {
  sanitization: SanitizationSettings;
  helpManualUrl?: string;
}

// Define the context type
interface AppSettingsContextType {
  settings: AppSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: (token?: string) => Promise<void>;
}

// Create the context with a default value
const AppSettingsContext = createContext<AppSettingsContextType>({
  settings: null,
  loading: true,
  error: null,
  refreshSettings: async () => {},
});

// Create a provider component
interface AppSettingsProviderProps {
  children: ReactNode;
}

export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();

  const fetchAppSettings = async (token?: string) => {
    try {
      // Check if settings are already in sessionStorage
      const cachedSettings = sessionStorage.getItem('appSettings');
      if (cachedSettings) {
        setSettings(JSON.parse(cachedSettings));
        setLoading(false);
        return;
      }

      // If not in sessionStorage, fetch from API with authentication if token is available
      const authToken = token || auth.user?.id_token;

      const response = await fetchWithAuth(ServiceEndpoints.FetchAppSettings, {}, authToken);

      if (!response.ok) {
        throw new Error(`Failed to fetch app settings: ${response.status}`);
      }

      const data: AppSettings = await response.json();

      // Store in sessionStorage
      sessionStorage.setItem('appSettings', JSON.stringify(data));

      setSettings(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching app settings:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  };

  // Function to refresh settings with a new token
  const refreshSettings = async (token?: string) => {
    setLoading(true);
    await fetchAppSettings(token);
  };

  useEffect(() => {
    if(auth.user?.access_token) {
      // Fetch and update the context state
      fetchAppSettings(auth.user?.id_token);
      
      // Use initAppSettings to ensure app settings are initialized with a token for the external service
      import('../services/AppSettingsService').then(({ initAppSettings }) => {
        initAppSettings(auth.user?.id_token);
      });
    }
  }, [auth.user?.access_token]);

  return (
    <AppSettingsContext.Provider value={{ settings, loading, error, refreshSettings }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

// Create a custom hook to use the app settings context
export const useAppSettings = () => useContext(AppSettingsContext);

export default AppSettingsContext;
